import React, { useState, useMemo, useEffect } from 'react';
import {
  MessageSquare,
  Trash2,
  ChevronsRight,
  ChevronsLeft,
  Plus,
  Sparkles,
  BookOpen,
} from 'lucide-react';
import { WarningDialog, success } from '@innovyze/stylovyze';
import { useDispatch } from 'react-redux';
import type {
  ChatHistoryProps,
  ChatHistoryConfirmState,
  Conversation,
} from '@/types/chat';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

export const ChatHistory = ({
  conversations,
  isOpen,
  onToggle,
  onNewChat,
  onOpenSkills,
  onOpenKnowledge,
  onSelectConversation,
  onDeleteConversation,
  onClearHistory,
}: ChatHistoryProps) => {
  const dispatch = useDispatch();
  const [confirmState, setConfirmState] = useState<ChatHistoryConfirmState>({
    type: null,
  });
  const [isClearing, setIsClearing] = useState(false);

  const recentConversations = useMemo(
    () =>
      [...conversations].sort(
        (a, b) =>
          new Date(b.requested_at).getTime() -
          new Date(a.requested_at).getTime(),
      ),
    [conversations],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onToggle('escape');
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onToggle]);

  const handleSelectConversation = (conversationId: string) => {
    onSelectConversation?.(conversationId);
    onToggle();
  };

  const handleDeleteClick = (
    e: React.MouseEvent,
    conversationId: string,
    initialPrompt: string,
  ) => {
    e.stopPropagation();
    trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.CHAT_HISTORY_DELETE_PROMPT, {
      conversation_id: conversationId,
    });
    onToggle();
    setConfirmState({ type: 'delete', conversationId, initialPrompt });
  };

  const handleClearHistoryClick = () => {
    trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.CHAT_HISTORY_CLEAR_PROMPT, {});
    onToggle();
    setConfirmState({ type: 'clear' });
  };

  const handleConfirm = async () => {
    if (confirmState.type === 'delete' && confirmState.conversationId) {
      await onDeleteConversation?.(confirmState.conversationId);
      dispatch(success('Conversation deleted'));
      setConfirmState({ type: null });
    } else if (confirmState.type === 'clear') {
      setIsClearing(true);
      try {
        await onClearHistory?.();
        dispatch(success('Chat history cleared'));
      } finally {
        setIsClearing(false);
        setConfirmState({ type: null });
      }
    }
  };

  const handleCancelConfirm = () => {
    trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.CHAT_HISTORY_CONFIRM_CANCEL, {
      dialog_type: confirmState.type ?? 'unknown',
    });
    setConfirmState({ type: null });
    onToggle();
  };

  const renderRecentList = (items: Conversation[]) => {
    if (items.length === 0) return null;
    return (
      <div className="space-y-1">
        {items.map(chat => (
          <div
            key={chat.conversation_id}
            className="group relative flex items-center mx-2">
            <button
              onClick={() => handleSelectConversation(chat.conversation_id)}
              className="w-full px-3 py-2.5 text-left hover:bg-[#E8EAEF] rounded-lg transition-colors flex items-center gap-3 pr-10">
              <span className="text-sm text-[#252F33] truncate flex-1">
                {chat.initial_prompt}
              </span>
            </button>
            <button
              onClick={e =>
                handleDeleteClick(e, chat.conversation_id, chat.initial_prompt)
              }
              className="absolute right-3 p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-[#E8EAEF] transition-all text-[#768D95] hover:text-[#252F33]"
              aria-label="Delete conversation">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  const sidebarWidth = isOpen ? 'w-72' : 'w-14';

  return (
    <>
      <div
        className={`${sidebarWidth} flex-shrink-0 flex flex-col bg-[#F3F5F9] transition-[width] duration-300 ease-in-out overflow-hidden border-r border-slate-200/80`}>
        <div className="flex items-center flex-shrink-0 bg-[#F3F5F9] border-b border-slate-200/80">
          <button
            onClick={() => onToggle('click')}
            className="flex items-center justify-center w-14 h-12 text-[#161618] hover:text-[#006EAF] hover:bg-[#E8EAEF] transition-colors flex-shrink-0"
            aria-label={isOpen ? 'Close chat history' : 'Open chat history'}>
            {isOpen ? (
              <ChevronsLeft className="h-5 w-5" />
            ) : (
              <ChevronsRight className="h-5 w-5" />
            )}
          </button>
        </div>

        {isOpen && (
          <>
            <div className="flex-shrink-0 px-3 pt-3 pb-2 bg-[#F3F5F9] space-y-2">
              <button
                onClick={onNewChat}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#E8EAEF] hover:bg-[#DEE1E8] text-[#252F33] transition-colors text-left">
                <Plus className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">New Chat</span>
              </button>
              {onOpenSkills && (
                <button
                  onClick={() => {
                    trackAdpWaypoint(
                      getBernoulliAdp(),
                      ADP_EVENTS.NAV_OPEN_SKILLS,
                      { source: 'sidebar_expanded' },
                    );
                    onOpenSkills();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-[#E8EAEF] text-[#252F33] transition-colors text-left">
                  <Sparkles className="h-5 w-5 flex-shrink-0 text-[#3C3C3C]" />
                  <span className="text-sm font-medium">Skills</span>
                </button>
              )}
              {onOpenKnowledge && (
                <button
                  onClick={() => {
                    trackAdpWaypoint(
                      getBernoulliAdp(),
                      ADP_EVENTS.NAV_OPEN_KNOWLEDGE,
                      { source: 'sidebar_expanded' },
                    );
                    onOpenKnowledge();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-[#E8EAEF] text-[#252F33] transition-colors text-left">
                  <BookOpen className="h-5 w-5 flex-shrink-0 text-[#007CA0]" />
                  <span className="text-sm font-medium">Knowledge</span>
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto py-2 bg-[#F3F5F9] min-h-0">
              <p className="px-4 py-1.5 text-xs font-semibold text-[#768D95] uppercase tracking-wider">
                Recent
              </p>
              {recentConversations.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-[#768D95]">No conversations yet</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Start a new chat to see it here
                  </p>
                </div>
              ) : (
                renderRecentList(recentConversations)
              )}
            </div>

            <div className="px-3 py-4 bg-[#F3F5F9] flex-shrink-0 border-t border-slate-200/80">
              <button
                onClick={handleClearHistoryClick}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm text-[#252F33] hover:bg-[#E8EAEF] rounded-lg transition-colors border border-slate-200 hover:border-slate-300">
                <Trash2 className="h-4 w-4 text-[#768D95]" />
                <span>Clear All History</span>
              </button>
              <p className="text-xs text-[#768D95] text-center mt-2">
                {conversations.length} conversation
                {conversations.length !== 1 ? 's' : ''}
              </p>
            </div>
          </>
        )}

        {!isOpen && (
          <div className="flex-shrink-0 bg-[#F3F5F9] p-2 space-y-1">
            <button
              onClick={onNewChat}
              className="flex items-center justify-center w-full h-10 rounded-lg hover:bg-[#E8EAEF] transition-colors text-[#161618] hover:text-[#006EAF]"
              aria-label="New chat">
              <Plus className="h-5 w-5" />
            </button>
            {onOpenSkills && (
              <button
                onClick={() => {
                  trackAdpWaypoint(
                    getBernoulliAdp(),
                    ADP_EVENTS.NAV_OPEN_SKILLS,
                    { source: 'sidebar_collapsed' },
                  );
                  onOpenSkills();
                }}
                className="flex items-center justify-center w-full h-10 rounded-lg hover:bg-[#E8EAEF] transition-colors text-[#3C3C3C]"
                aria-label="Skills">
                <Sparkles className="h-5 w-5" />
              </button>
            )}
            {onOpenKnowledge && (
              <button
                onClick={() => {
                  trackAdpWaypoint(
                    getBernoulliAdp(),
                    ADP_EVENTS.NAV_OPEN_KNOWLEDGE,
                    { source: 'sidebar_collapsed' },
                  );
                  onOpenKnowledge();
                }}
                className="flex items-center justify-center w-full h-10 rounded-lg hover:bg-[#E8EAEF] transition-colors text-[#007CA0]"
                aria-label="Knowledge">
                <BookOpen className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </div>

      <WarningDialog
        open={confirmState.type === 'delete'}
        title="Delete Conversation"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}>
        <div>
          <p className="mb-2">
            Are you sure you want to delete this conversation?
          </p>
          {confirmState.initialPrompt && (
            <p className="text-sm text-gray-500 bg-gray-100 p-2 rounded truncate">
              &ldquo;{confirmState.initialPrompt}&rdquo;
            </p>
          )}
          <p className="mt-2 text-sm text-gray-500">
            This action cannot be undone.
          </p>
        </div>
      </WarningDialog>

      <div className={isClearing ? 'pointer-events-none' : ''}>
        <WarningDialog
          open={confirmState.type === 'clear' || isClearing}
          title={isClearing ? 'Clearing History...' : 'Clear Chat History'}
          confirmText={isClearing ? 'Clearing...' : 'Clear All'}
          cancelText="Cancel"
          onConfirm={handleConfirm}
          onCancel={handleCancelConfirm}>
          {isClearing ? (
            <div className="flex items-center gap-3 py-2">
              <div className="w-5 h-5 rounded-full border-2 border-slate-200 border-t-[#3C3C3C] animate-spin" />
              <span>Clearing all conversations...</span>
            </div>
          ) : (
            'Are you sure you want to clear all chat history? This will delete all your conversations and cannot be undone.'
          )}
        </WarningDialog>
      </div>
    </>
  );
};
